import { useEffect, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';

import { scale } from '@components/frame';
import { isPhone } from '@helpers';

import {
  WELCOME_CONTENT_PUSH_MODAL,
  WELCOME_CONTENT_PUSH_PHONE,
  WELCOME_FADE_MS,
  WELCOME_LOGO_PUSH_MODAL,
  WELCOME_LOGO_PUSH_PHONE,
  WELCOME_TRANSITION_MS,
  isCenteredLogoStep,
  type StepIndex,
} from '../constants';

export type UseManageOnboardingWizardModalParams = {
  currentStep: StepIndex;
};

export const useManageOnboardingWizardModal = ({ currentStep }: UseManageOnboardingWizardModalParams) => {
  const welcomeProgress = useRef(
    new Animated.Value(isCenteredLogoStep(currentStep) ? 1 : 0),
  ).current;
  // Held invisible during step swap so WizardStepTransition's internal crossfade doesn't
  // run mid-welcome-animation.
  const contentOpacity = useRef(new Animated.Value(1)).current;
  const [renderedStepKey, setRenderedStepKey] = useState<StepIndex>(currentStep);
  const mountedRef = useRef(true);
  const fadeAnim = useRef<Animated.CompositeAnimation | null>(null);
  const slideAnim = useRef<Animated.CompositeAnimation | null>(null);

  useEffect(
    () => () => {
      mountedRef.current = false;
      fadeAnim.current?.stop();
      slideAnim.current?.stop();
    },
    [],
  );

  useEffect(() => {
    const wasCentered = isCenteredLogoStep(renderedStepKey);
    const willBeCentered = isCenteredLogoStep(currentStep);
    const crossesCentered = wasCentered !== willBeCentered;

    if (!crossesCentered) {
      setRenderedStepKey(currentStep);
      return;
    }

    fadeAnim.current?.stop();
    slideAnim.current?.stop();

    const fadeOut = Animated.timing(contentOpacity, {
      toValue: 0,
      duration: WELCOME_FADE_MS,
      useNativeDriver: true,
    });
    fadeAnim.current = fadeOut;
    fadeOut.start(({ finished }) => {
      if (!finished || !mountedRef.current) return;
      setRenderedStepKey(currentStep);
      const fadeIn = Animated.timing(contentOpacity, {
        toValue: 1,
        duration: WELCOME_FADE_MS,
        useNativeDriver: true,
      });
      fadeAnim.current = fadeIn;
      fadeIn.start();
    });

    const slide = Animated.timing(welcomeProgress, {
      toValue: willBeCentered ? 1 : 0,
      duration: WELCOME_TRANSITION_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    slideAnim.current = slide;
    slide.start();
    // renderedStepKey intentionally omitted: it's only mutated *by* this effect, including
    // it would re-fire on every swap and the closure already reads the right value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const isRenderedCentered = isCenteredLogoStep(renderedStepKey);
  const stepTransitionKey = isRenderedCentered ? 'centered' : 'steps';

  const logoTranslateY = welcomeProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, scale(isPhone ? WELCOME_LOGO_PUSH_PHONE : WELCOME_LOGO_PUSH_MODAL)],
  });
  const contentTranslateY = welcomeProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, scale(isPhone ? WELCOME_CONTENT_PUSH_PHONE : WELCOME_CONTENT_PUSH_MODAL)],
  });
  const chromeOpacity = welcomeProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0],
  });

  return {
    renderedStepKey,
    stepTransitionKey,
    isRenderedCentered,
    contentOpacity,
    logoTranslateY,
    contentTranslateY,
    chromeOpacity,
  };
};
